import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Canonical } from '@/components/seo/Canonical';
import { SupportButton } from '@/components/layout/SupportButton';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { RequireAuth } from '@/app/guards/RequireAuth';
import { RequireRole } from '@/app/guards/RequireRole';
import HomePage from '@/pages/HomePage';
import SearchPage from '@/pages/SearchPage';
import SignInPage from '@/pages/auth/SignInPage';
import SignUpPage from '@/pages/auth/SignUpPage';
import NotFoundPage from '@/pages/NotFoundPage';

// Chargement à la demande : un visiteur qui cherche un véhicule ne télécharge
// ni le tunnel de réservation ni le code des trois tableaux de bord.
const VehiclePage = lazy(() => import('@/pages/VehiclePage'));
const BookingPage = lazy(() => import('@/pages/BookingPage'));
const CityLandingPage = lazy(() => import('@/pages/CityLandingPage'));
const HowItWorksPage = lazy(() => import('@/pages/HowItWorksPage'));
const BecomePartnerPage = lazy(() => import('@/pages/BecomePartnerPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const CustomerRoutes = lazy(() => import('@/pages/customer/CustomerRoutes'));
const PartnerRoutes = lazy(() => import('@/pages/partner/PartnerRoutes'));
const AdminRoutes = lazy(() => import('@/pages/admin/AdminRoutes'));
const SellerRoutes = lazy(() => import('@/pages/seller/SellerRoutes'));
const ListingsPage = lazy(() => import('@/pages/ListingsPage'));
const ListingDetailPage = lazy(() => import('@/pages/ListingDetailPage'));
const BecomeSellerPage = lazy(() => import('@/pages/BecomeSellerPage'));
const ProvidersPage = lazy(() => import('@/pages/ProvidersPage'));
const ProviderPage = lazy(() => import('@/pages/ProviderPage'));
const BecomeProviderPage = lazy(() => import('@/pages/BecomeProviderPage'));
const ProviderRoutes = lazy(() => import('@/pages/provider/ProviderRoutes'));
const LegalNoticePage = lazy(() => import('@/pages/legal/LegalNoticePage'));
const TermsPage = lazy(() => import('@/pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/legal/PrivacyPage'));

export default function App() {
  return (
    <Suspense fallback={<FullPageSpinner />}>
      {/* Chaque page déclare sa propre adresse de référence. */}
      <Canonical />
      <Routes>
        {/* ---------------------------------------------------------------- */}
        {/* Espace public                                                     */}
        {/* ---------------------------------------------------------------- */}
        <Route element={<PublicLayout />}>
          <Route index element={<HomePage />} />
          <Route path="recherche" element={<SearchPage />} />
          <Route path="vehicule/:vehicleId" element={<VehiclePage />} />
          <Route path="comment-ca-marche" element={<HowItWorksPage />} />
          <Route path="devenir-partenaire" element={<BecomePartnerPage />} />

          {/* Petites annonces : un domaine distinct de la location. */}
          <Route path="voitures-a-vendre" element={<ListingsPage />} />
          <Route path="voitures-a-vendre/:listingId" element={<ListingDetailPage />} />
          <Route path="vendre-ma-voiture" element={<BecomeSellerPage />} />

          {/* Garages et lavages : deux annuaires, un seul écran, une seule
              fiche. Un établissement qui fait les deux apparaît des deux
              côtés sans être inscrit deux fois. */}
          <Route path="garages" element={<ProvidersPage kind="garage" />} />
          <Route path="lavage-auto" element={<ProvidersPage kind="carwash" />} />
          <Route path="services/:providerSlug" element={<ProviderPage />} />
          <Route path="devenir-prestataire" element={<BecomeProviderPage />} />

          {/* Pages légales : accessibles sans compte, par obligation. */}
          <Route path="mentions-legales" element={<LegalNoticePage />} />
          <Route path="conditions-generales" element={<TermsPage />} />
          <Route path="confidentialite" element={<PrivacyPage />} />

          <Route path="connexion" element={<SignInPage />} />
          <Route path="inscription" element={<SignUpPage />} />
          <Route path="mot-de-passe-oublie" element={<ForgotPasswordPage />} />

          {/* Le tunnel de réservation exige une session. */}
          <Route path="reservation/:vehicleId" element={<BookingPage />} />

          {/* Les pages villes : « /location-voiture-abidjan ».
              React Router 6 n'accepte pas « location-voiture-:citySlug » — un
              paramètre doit occuper un segment entier —, ce qui renvoyait
              toutes ces pages sur une 404. On capte le segment complet, et
              CityLandingPage vérifie le préfixe puis rend « introuvable » si ce
              n'en est pas une. Placée en avant-dernier : toutes les routes
              fixes déclarées plus haut gardent la priorité. */}
          <Route path=":citySegment" element={<CityLandingPage />} />

          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* ---------------------------------------------------------------- */}
        {/* Espaces privés                                                    */}
        {/* ---------------------------------------------------------------- */}
        <Route element={<RequireAuth />}>
          <Route element={<RequireRole allow={['customer', 'admin']} />}>
            <Route path="compte/*" element={<CustomerRoutes />} />
          </Route>

          <Route element={<RequireRole allow={['partner', 'admin']} />}>
            <Route path="partenaire/*" element={<PartnerRoutes />} />
          </Route>

          <Route element={<RequireRole allow={['seller', 'admin']} />}>
            <Route path="vendeur/*" element={<SellerRoutes />} />
          </Route>

          <Route element={<RequireRole allow={['provider', 'admin']} />}>
            <Route path="prestataire/*" element={<ProviderRoutes />} />
          </Route>

          <Route element={<RequireRole allow={['admin']} />}>
            <Route path="admin/*" element={<AdminRoutes />} />
          </Route>
        </Route>
      </Routes>

      {/* Le bouton d'aide, en bas à droite. Monté ici, hors des routes : il
          doit suivre le visiteur partout, du catalogue au tableau de bord.
          Placé APRÈS <Routes> pour qu'il se dessine au-dessus des pages, et
          sous l'en-tête (z-30 contre z-40) pour ne jamais recouvrir le menu
          mobile quand il est ouvert. */}
      <SupportButton />
    </Suspense>
  );
}
